import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';

export interface DeleteReasonModalProps {
  /** Pro/template id being deleted — shown in the header for confirmation. */
  pronumber: number | string;
  /** True for a template (labels/copy differ slightly). */
  isTemplate?: boolean;
  /** Saving state — disables the buttons and shows "Saving…" while the delete persists. */
  saving?: boolean;
  /** Cancel: caller reverts the Deleted flag back to 'N'. */
  onCancel: () => void;
  /** Confirm with a non-empty reason: caller immediately saves the delete. */
  onConfirm: (reason: string) => void;
}

/**
 * "Reason for Delete" prompt, mirroring the legacy CMS modal. Shown when a user
 * flips the Addl Info "Deleted" field to Y on a record that passed the
 * delete-eligibility check. Save is disabled until a reason is entered; Cancel
 * leaves the record un-deleted.
 */
export function DeleteReasonModal({
  pronumber,
  isTemplate = false,
  saving = false,
  onCancel,
  onConfirm,
}: DeleteReasonModalProps) {
  const [reason, setReason] = useState('');
  const isValid = reason.trim() !== '';
  const noun = isTemplate ? 'template' : 'order';

  const handleConfirm = () => {
    if (!isValid || saving) return;
    onConfirm(reason.trim());
  };

  // Alt+S / Alt+C, matching the footer chords on the Pro drawer behind us. This
  // modal only mounts while it's open, so the layer claim is unconditional —
  // and claiming it is what stops Alt+S from reaching the drawer's Save.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useAltHotkeys(
    {
      S: isValid && !saving ? handleConfirm : null,
      C: !saving ? onCancel : null,
    },
    rootRef,
    { layer: true },
  );

  // Mounted only while open, so the drag offset resets on unmount.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Reason for Delete"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-scrim/50"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
      >
        {/* Header */}
        <ModalHeader
          title="Reason for Delete"
          onClose={saving ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
          closeLabel="Cancel delete"
        />

        {/* Content */}
        <div className="px-6 py-4 space-y-3">
          <p className="text-label">
            You are about to delete {noun} <span className="font-semibold">{pronumber}</span>.
            Enter a reason to continue; this saves immediately.
          </p>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-yellow-50 dark:bg-gray-700 text-body placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            rows={4}
            placeholder="Enter a reason for deleting this record…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
            autoFocus
            aria-label="Reason for delete"
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DeleteReasonModal;
